import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useAuth,
  useOverviewMetrics,
  useTherapistBookings,
  useUnreadCount,
  type Booking,
} from '@theraply/api-client';
import ExpirationWarning from '../components/ExpirationWarning';
import {
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  StarIcon,
  UserGroupIcon,
  VideoCameraIcon,
  ClockIcon,
  Cog6ToothIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

function getDateRange() {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setMonth(today.getMonth() - 1);
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: today.toISOString().split('T')[0],
  };
}

function formatCurrency(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

function formatTime(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${minutes} ${ampm}`;
}

function isUpcoming(booking: Booking): boolean {
  if (booking.status !== 'CONFIRMED') return false;
  const now = new Date();
  const bookingDate = new Date(`${booking.slotDate}T${booking.slotTime}`);
  return bookingDate > now;
}

export default function DashboardPage() {
  const { user } = useAuth();
  const dateRange = getDateRange();

  const overviewQuery = useOverviewMetrics(dateRange);
  const bookingsQuery = useTherapistBookings();
  const unreadCountQuery = useUnreadCount();

  const upcomingSessions = (bookingsQuery.data?.bookings || [])
    .filter(isUpcoming)
    .sort((a, b) => {
      const dateA = new Date(`${a.slotDate}T${a.slotTime}`);
      const dateB = new Date(`${b.slotDate}T${b.slotTime}`);
      return dateA.getTime() - dateB.getTime();
    })
    .slice(0, 5);

  const unreadCount = unreadCountQuery.data ?? 0;
  const isLoading = overviewQuery.isLoading || bookingsQuery.isLoading;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-sm text-gray-600">
          Welcome back, {user?.firstName}! Here's an overview of your practice.
        </p>
      </div>

      {/* Expiration Warning Banner */}
      <div className="mb-6">
        <ExpirationWarning />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <KPICard
          label="Total Sessions"
          value={overviewQuery.data?.totalSessions ?? 0}
          icon={<ChartBarIcon className="h-6 w-6 text-blue-600" />}
          loading={overviewQuery.isLoading}
          bgColor="bg-blue-50"
        />
        <KPICard
          label="Revenue This Month"
          value={overviewQuery.data?.totalRevenue ?? 0}
          formatter={formatCurrency}
          icon={<CurrencyDollarIcon className="h-6 w-6 text-green-600" />}
          loading={overviewQuery.isLoading}
          bgColor="bg-green-50"
        />
        <KPICard
          label="Average Rating"
          value={overviewQuery.data?.averageRating ?? 0}
          formatter={(v) => (v > 0 ? v.toFixed(1) : '--')}
          icon={<StarIcon className="h-6 w-6 text-yellow-600" />}
          loading={overviewQuery.isLoading}
          bgColor="bg-yellow-50"
        />
        <KPICard
          label="Active Clients"
          value={overviewQuery.data?.uniqueClients ?? 0}
          icon={<UserGroupIcon className="h-6 w-6 text-indigo-600" />}
          loading={overviewQuery.isLoading}
          bgColor="bg-indigo-50"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Upcoming Sessions */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Upcoming Sessions
                </h2>
              </div>
              <Link
                to="/sessions"
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                View all <ArrowRightIcon className="h-4 w-4" />
              </Link>
            </div>

            <div className="divide-y divide-gray-100">
              {bookingsQuery.isLoading ? (
                <div className="px-6 py-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                  <p className="mt-3 text-sm text-gray-500">Loading sessions...</p>
                </div>
              ) : bookingsQuery.error ? (
                <div className="px-6 py-8 text-center">
                  <p className="text-sm text-red-600">Failed to load sessions.</p>
                  <button
                    onClick={() => bookingsQuery.refetch()}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    Retry
                  </button>
                </div>
              ) : upcomingSessions.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <CalendarIcon className="h-12 w-12 text-gray-300 mx-auto" />
                  <h3 className="mt-3 text-sm font-medium text-gray-900">
                    No upcoming sessions
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You don't have any sessions scheduled.
                  </p>
                </div>
              ) : (
                upcomingSessions.map((booking) => (
                  <div
                    key={booking.id}
                    className="px-6 py-4 hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                        <VideoCameraIcon className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {booking.consumerName || `Client #${booking.consumerId}`}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">
                            {formatDate(booking.slotDate)}
                          </span>
                          <span className="text-xs text-gray-400">|</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <ClockIcon className="h-3 w-3" />
                            {formatTime(booking.slotTime)}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {booking.sessionType === 'INTAKE'
                              ? 'Intake'
                              : booking.sessionType === 'CRISIS'
                              ? 'Crisis'
                              : 'Regular'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/session/${booking.id}`}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                    >
                      <VideoCameraIcon className="h-4 w-4 mr-1" />
                      Join
                    </Link>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions Sidebar */}
        <div className="space-y-6">
          {/* Unread Messages */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                Messages
              </h3>
              {unreadCount > 0 && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {unreadCount} unread
                </span>
              )}
            </div>
            <Link
              to="/messages"
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-700">
                {unreadCount > 0
                  ? `You have ${unreadCount} unread message${unreadCount > 1 ? 's' : ''}`
                  : 'No new messages'}
              </span>
            </Link>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <QuickAction
                to="/availability"
                icon={<CalendarIcon className="h-5 w-5" />}
                label="Manage Availability"
              />
              <QuickAction
                to="/messages"
                icon={<ChatBubbleLeftRightIcon className="h-5 w-5" />}
                label="View Messages"
              />
              <QuickAction
                to="/analytics"
                icon={<ChartBarIcon className="h-5 w-5" />}
                label="View Analytics"
              />
              <QuickAction
                to="/clients"
                icon={<UserGroupIcon className="h-5 w-5" />}
                label="View Clients"
              />
              <QuickAction
                to="/settings"
                icon={<Cog6ToothIcon className="h-5 w-5" />}
                label="Settings"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPICard({
  label,
  value,
  formatter,
  icon,
  loading,
  bgColor,
}: {
  label: string;
  value: number;
  formatter?: (v: number) => string;
  icon: React.ReactNode;
  loading: boolean;
  bgColor: string;
}) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          {loading ? (
            <div className="mt-2 h-8 w-24 bg-gray-200 rounded animate-pulse" />
          ) : (
            <p className="mt-2 text-3xl font-bold text-gray-900">
              {formatter ? formatter(value) : value}
            </p>
          )}
        </div>
        <div className={`${bgColor} rounded-full p-3`}>{icon}</div>
      </div>
    </div>
  );
}

function QuickAction({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors text-gray-700 hover:text-gray-900"
    >
      <span className="text-gray-400">{icon}</span>
      <span className="text-sm font-medium">{label}</span>
      <ArrowRightIcon className="h-4 w-4 ml-auto text-gray-300" />
    </Link>
  );
}
